// La columna diaria incluye también las horas fuera de las franjas visibles.
import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const EMPTY_ROWS = [];
const DATE_WIDTH = 112;
const TYPE_WIDTH = 112;
const HOUR_WIDTH = 52;
const FOOTER_HEIGHT = 44;
const TOTAL_WIDTH = 100;
const lightMovement = {
  Ent: { label: "Entradas", background: "#ecfdf5", color: "#166534" },
  Sal: { label: "Salidas", background: "#eff6ff", color: "#1d4ed8" },
};

const formatDate = (value) => {
  // La fecha agrupada viene como YYYY-MM-DD: no convertirla a UTC y restarle un día.
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value);
};
const formatNumber = (value) => Number(value || 0).toLocaleString("es-CO");
const fixedCell = (left, background, zIndex = 2) => ({
  position: "sticky",
  left,
  bgcolor: background,
  zIndex,
});

const FrecuenciaTable = ({ data, loading }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const movement = dark
    ? {
        Ent: { label: "Entradas", background: "#193e38", color: "#9be3ba" },
        Sal: { label: "Salidas", background: "#233c60", color: "#b2d3ff" },
      }
    : lightMovement;
  const rows = Array.isArray(data?.rows) ? data.rows : EMPTY_ROWS;
  const days = useMemo(() => {
    const groups = new Map();
    rows.forEach((row) => {
      if (!["Ent", "Sal"].includes(row.tipo)) return;
      if (!groups.has(row.fecha)) groups.set(row.fecha, {});
      groups.get(row.fecha)[row.tipo] = row;
    });
    return [...groups.entries()].map(([date, group]) => ({
      date,
      rows: [group.Ent, group.Sal].filter(Boolean),
    }));
  }, [rows]);
  const hours = (data?.hours || []).filter((hour) => hour >= 4 && hour <= 23);

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }} role="status">
        <CircularProgress size={30} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Cargando frecuencia...
        </Typography>
      </Box>
    );
  }
  if (!days.length) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="subtitle1" fontWeight={600}>
          No hay datos para el rango seleccionado
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Ajuste las fechas o los tipos de tarjeta y aplique los filtros.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Frecuencia por hora
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Entradas y salidas · 04:00 a 23:00
          </Typography>
        </Box>
        <Chip
          size="small"
          variant="outlined"
          label={`${days.length} ${days.length === 1 ? "día" : "días"}`}
        />
      </Box>
      <TableContainer
        role="region"
        aria-label="Tabla de frecuencia por hora"
        tabIndex={0}
        sx={{
          maxHeight: "min(65vh, 640px)",
          overflow: "auto",
          borderTop: `1px solid ${theme.palette.divider}`,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { height: 7, width: 7 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#cbd5e1", borderRadius: 4 },
          "&:focus-visible": { outline: "2px solid", outlineColor: "info.main", outlineOffset: -2 },
        }}
      >
        <Table
          stickyHeader
          size="small"
          aria-label="Entradas y salidas por fecha y hora"
          sx={{
            minWidth: DATE_WIDTH + TYPE_WIDTH + HOUR_WIDTH * hours.length + TOTAL_WIDTH,
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0,
            "& .MuiTableCell-root": {
              px: 1,
              py: 1.25,
              fontSize: "0.78rem",
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
              borderBottom: `1px solid ${theme.palette.divider}`,
              color: "text.primary",
              textAlign: "center",
            },
          }}
        >
          <colgroup>
            <col style={{ width: DATE_WIDTH }} />
            <col style={{ width: TYPE_WIDTH }} />
            {hours.map((hour) => (
              <col key={hour} style={{ width: HOUR_WIDTH }} />
            ))}
            <col style={{ width: TOTAL_WIDTH }} />
          </colgroup>
          <TableHead
            sx={{
              display: "table-header-group",
              p: 0,
              "& .MuiTableCell-root": {
                bgcolor: "#16364b",
                color: "#fff",
                height: 46,
                fontWeight: 700,
                borderBottomColor: "#16364b",
              },
            }}
          >
            <TableRow>
              <TableCell scope="col" sx={fixedCell(0, "#16364b", 5)}>
                Fecha
              </TableCell>
              <TableCell
                scope="col"
                sx={{ ...fixedCell(DATE_WIDTH, "#16364b", 5), boxShadow: "3px 0 5px -4px #64748b" }}
              >
                Movimiento
              </TableCell>
              {hours.map((hour) => (
                <TableCell key={hour} scope="col">
                  {String(hour).padStart(2, "0")}:00
                </TableCell>
              ))}
              <TableCell scope="col">Total día</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {days.map((day, dayIndex) => {
              const background = dark
                ? dayIndex % 2
                  ? "#26324a"
                  : theme.palette.background.paper
                : dayIndex % 2
                ? "#f8fafc"
                : "#fff";
              return day.rows.map((row, rowIndex) => (
                <TableRow
                  key={`${day.date}-${row.tipo}`}
                  sx={{
                    bgcolor: background,
                    "&:hover .MuiTableCell-root": { bgcolor: dark ? "#35455e" : "#f1f5f9" },
                  }}
                >
                  {rowIndex === 0 && (
                    <TableCell
                      component="th"
                      scope="rowgroup"
                      rowSpan={day.rows.length}
                      sx={{ ...fixedCell(0, background), fontWeight: 700, verticalAlign: "middle" }}
                    >
                      {formatDate(day.date)}
                    </TableCell>
                  )}
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      ...fixedCell(DATE_WIDTH, background),
                      boxShadow: "3px 0 5px -4px #94a3b8",
                    }}
                  >
                    <Chip
                      label={movement[row.tipo].label}
                      size="small"
                      sx={{
                        height: 24,
                        minWidth: 78,
                        bgcolor: movement[row.tipo].background,
                        color: movement[row.tipo].color,
                        fontWeight: 600,
                        fontSize: "0.72rem",
                      }}
                    />
                  </TableCell>
                  {hours.map((hour) => {
                    const value = row[hour] || 0;
                    return (
                      <TableCell
                        key={hour}
                        sx={{
                          color: value > 0 ? "text.primary" : "text.secondary",
                          fontWeight: value > 0 ? 600 : 400,
                          bgcolor: value > 0 ? movement[row.tipo].background : undefined,
                        }}
                      >
                        {formatNumber(value)}
                      </TableCell>
                    );
                  })}
                  <TableCell sx={{ fontWeight: 700, bgcolor: movement[row.tipo].background }}>
                    {formatNumber(
                      row.total ??
                        Object.keys(row)
                          .filter((key) => /^\d+$/.test(key))
                          .reduce((sum, key) => sum + Number(row[key] || 0), 0)
                    )}
                  </TableCell>
                </TableRow>
              ));
            })}
          </TableBody>
          <TableFooter>
            {["Ent", "Sal"].map((type, index) => {
              const style = movement[type];
              const values = type === "Ent" ? data.totals?.entries : data.totals?.exits;
              const bottom = index === 0 ? FOOTER_HEIGHT : 0;
              return (
                <TableRow
                  key={type}
                  sx={{
                    "& .MuiTableCell-root": {
                      position: "sticky",
                      bottom,
                      zIndex: 3,
                      height: FOOTER_HEIGHT,
                      py: 1,
                      bgcolor: style.background,
                      color: style.color,
                      fontWeight: 700,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                >
                  <TableCell component="th" scope="row" sx={{ left: 0, zIndex: "4 !important" }}>
                    {type === "Ent" ? "Total ingresos" : "Total salidas"}
                  </TableCell>
                  <TableCell
                    sx={{
                      left: DATE_WIDTH,
                      zIndex: "4 !important",
                      boxShadow: "3px 0 5px -4px #94a3b8",
                    }}
                  >
                    {style.label}
                  </TableCell>
                  {hours.map((hour) => (
                    <TableCell key={hour}>{formatNumber(values?.[hour])}</TableCell>
                  ))}
                  <TableCell>
                    {formatNumber(
                      (type === "Ent" ? data.totalEntries : data.totalExits) ??
                        Object.values(values || {}).reduce(
                          (sum, value) => sum + Number(value || 0),
                          0
                        )
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableFooter>
        </Table>
      </TableContainer>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", px: { xs: 2, sm: 3 }, py: 1.5 }}
      >
        Los totales diarios incluyen el día completo (00:00 a 23:59). Las columnas muestran las
        horas de 04 a 23.
      </Typography>
    </Box>
  );
};
FrecuenciaTable.propTypes = {
  data: PropTypes.shape({
    rows: PropTypes.array,
    totals: PropTypes.shape({ entries: PropTypes.object, exits: PropTypes.object }),
    hours: PropTypes.array,
    totalEntries: PropTypes.number,
    totalExits: PropTypes.number,
  }),
  loading: PropTypes.bool,
};
FrecuenciaTable.defaultProps = { data: null, loading: false };
export default FrecuenciaTable;
